/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { createToast } from "./createToast";

/**
 * Erro de regra de negócio no formato `{ code, message }`, com a mensagem já
 * escrita para o usuário final.
 *
 * ⚠️ Verificado contra a API do PR !710 em 2026-09-13: as travas de estrutura
 * do Tipo de Requerimento NÃO usam este formato. O backend monta
 * `new Error("etapa", "mensagem")`, mas serializa como `{ "etapa": ["mensagem"] }`
 * — chave → array, que o laço abaixo já trata e já exibe a mensagem real. O
 * contrato de API descrevia `{ code, message }`; a implementação diverge.
 *
 * Este tratamento fica como caminho defensivo: é barato, não conflita com o
 * laço (só dispara quando `code` e `message` são ambos string) e evita que uma
 * rota que venha a adotar o formato caia no genérico "Não foi possivel
 * realizar a operação", descartando a explicação da recusa.
 */
interface ErroDeRegra {
  code: string;
  message: string;
}

/** Devolve o `{ code, message }` do corpo, ou null quando não é esse formato. */
function erroDeRegra(data: any): ErroDeRegra | null {
  if (!data || typeof data !== "object") return null;

  const { code, message } = data;
  if (typeof code !== "string" || !code) return null;
  if (typeof message !== "string" || !message.trim()) return null;

  return { code, message: message.trim() };
}

/**
 * Código do erro de regra, quando a resposta trouxer um.
 *
 * Serve para a tela DECIDIR algo — a mensagem em si o `formatError` já mostra.
 */
export function extrairCodigoErro(error: any): string | null {
  return erroDeRegra(error?.response?.data)?.code ?? null;
}

export function formatError(error: any): string[] {
  const errorNotDefined = "Não foi possivel realizar a operação";
  const toast = createToast();

  if (error.response) {
    if (error.response.status === 400) {
      // O backend escreveu uma mensagem para o usuário — mostrar essa, não a
      // genérica. Sem isto o `code` (string) cai no else do laço abaixo e a
      // explicação real da recusa é descartada.
      const regra = erroDeRegra(error.response.data);
      if (regra) {
        toast({
          title: regra.message,
          status: "error",
          position: "bottom",
          duration: 8000,
          isClosable: true
        });
        return [regra.message];
      }

      if (error.response.data.errors) {
        toast({
          title: errorNotDefined,
          status: "error",
          position: "bottom"
        });
        return [errorNotDefined];
      }

      const errorList = [];
      for (const key of Object.keys(error.response.data)) {
        if (typeof error.response.data[key] === typeof []) {
          for (const errorMessage of error.response.data[key]) {
            errorList.push(errorMessage);
            toast({
              title: errorMessage,
              status: "info",
              position: "bottom"
            });
          }
        } else {
          toast({
            title: errorNotDefined,
            status: "error",
            position: "bottom"
          });
          return [errorNotDefined];
        }
      }
      return errorList;
    }

    if (error.response.status === 403) {
      toast({
        title: "Operação não autorizada.",
        status: "info",
        position: "bottom"
      });
      return [error.response.statusText];
    }

    if (error.response.status === 401) {
      return [error.response.statusText];
    }

    if (error.response.status === 404) {
      return [error.response.statusText];
    }
  }
  toast({
    title: errorNotDefined,
    status: "error",
    position: "bottom"
  });
  return [error.response.statusText];
}
